import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, Signal, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChipSet } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep, isEqual, omit } from 'lodash';
import { Observable, Subject, combineLatest, defer, delay, merge, of, startWith, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ELoadStatus } from '../../../state/state.interface';
import { FilterCardAddComponent } from './filter-card-add/filter-card-add.component';
import { FilterCardChipComponent } from './filter-card-chip/filter-card-chip.component';
import { FilterCardManager } from './filter-card-manager';
import { TFilterCardFieldConfig, TFilterCardValue } from './filter-card.interface';

@Component({
  imports: [
    AsyncPipe,
    FilterCardAddComponent,
    FilterCardChipComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatChipSet,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-filter-card',
  styleUrl: './filter-card.component.scss',
  templateUrl: './filter-card.component.html',
})
export class FilterCardComponent<Name extends string> implements OnInit {
  public readonly manager = input.required<FilterCardManager<Name>>();

  /**
   * Which field `FilterCardAddComponent` currently has open for drafting (`null` when its picker is
   * closed) — see `activeFields` below and `FilterCardAddComponent.draftingFieldChange`'s doc comment.
   */
  public readonly addDraftFieldName = signal<Name | null>(null);

  /**
   * The currently-active field configs, in `activeFieldNames()` order — what renders as chips.
   * Excludes whatever `FilterCardAddComponent` is currently drafting: `pendingChange` activates a
   * field the moment the user starts interacting with it (so the Apply bar can appear before the
   * field is "done"), but `FilterCardAddComponent` is already rendering that field's input itself —
   * without this filter, the same field would render twice (once as a real chip, once as the open
   * add-picker) until the draft is committed or cancelled.
   */
  public readonly activeFields: Signal<TFilterCardFieldConfig<Name>[]> = computed(() => {
    const fieldsByName = new Map(
      this.manager()
        .fields()
        .map((field) => [field.name, field]),
    );
    const draftingName = this.addDraftFieldName();

    return this.manager()
      .activeFieldNames()
      .filter((name) => name !== draftingName)
      .map((name) => fieldsByName.get(name))
      .filter((field): field is TFilterCardFieldConfig<Name> => Boolean(field));
  });
  /** The pool minus whatever's already active — what the "+ Add Filter" menu offers. */
  public readonly pool: Signal<TFilterCardFieldConfig<Name>[]> = computed(() => {
    const activeNames = new Set(this.manager().activeFieldNames());

    return this.manager()
      .fields()
      .filter((field) => !activeNames.has(field.name));
  });

  /**
   * One control per *active* field. `linkedSignal`'s `previous` carries the same `FormGroup` forward
   * across recomputations (instead of a private mutable field), adding a control when a field joins
   * `activeFieldNames()` and removing one when it drops out — chips are add/remove, not add-only.
   */
  public readonly formGroup: Signal<FormGroup<Record<string, FormControl>>> = linkedSignal<
    Name[],
    FormGroup<Record<string, FormControl>>
  >({
    computation: (activeFieldNames, previous) => {
      const formGroup = previous?.value ?? new FormGroup<Record<string, FormControl>>({});
      const { initialValue } = this.manager();
      const activeSet = new Set(activeFieldNames);

      for (const name of activeFieldNames) {
        if (!formGroup.contains(name)) {
          formGroup.addControl(name, new FormControl(initialValue[name] ?? null));
        }
      }

      for (const existingName of Object.keys(formGroup.controls)) {
        if (!activeSet.has(existingName as Name)) {
          (formGroup as FormGroup).removeControl(existingName);
        }
      }

      return formGroup;
    },
    source: () => this.manager().activeFieldNames(),
  });
  public readonly controls: Signal<Record<Name, FormControl>> = computed(
    () => this.formGroup().controls as Record<Name, FormControl>,
  );
  // `defer` delays `buildShowApply()` (which reads the required `manager` input) until the `| async`
  // pipe subscribes, well after ngOnInit — reading it eagerly here would throw NG0950, since Angular
  // only binds required input signals after the constructor runs.
  public readonly showApply: Observable<boolean> = defer(() => this.buildShowApply());

  private readonly applied$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.subscribeToValuePatches();
    this.apply();
  }

  public apply(): void {
    const formValue = cloneDeep(this.formGroup().value) as TFilterCardValue<Name>;

    this.manager().appliedValue.set(this.mergeIntoAppliedValue(formValue));
    this.applied$.next();
    // A fresh Symbol() matches no open chip's/add-form's own identity, so this closes all of them —
    // see FilterCardManager.oneOpenFormAtATime$'s doc comment.
    this.manager().oneOpenFormAtATime$.next(Symbol());
  }

  public setAsDefault(): void {
    this.apply();
    this.manager().setAsDefault$.next(cloneDeep(this.manager().appliedValue()) as TFilterCardValue<Name>);
  }

  /** A chip was removed: an empty value deactivates it (see `FilterCardManager.patchValue`), which
   * drops its control on the next `formGroup()` read — same channel as any other programmatic push. */
  public onChipRemove(name: Name): void {
    this.manager().patchValue({ [name]: null } as TFilterCardValue<Name>);
  }

  public onChipEdit(name: Name, value: unknown): void {
    this.manager().patchValue({ [name]: value } as TFilterCardValue<Name>);
  }

  public onAdd({ name, value }: { name: Name; value: unknown }): void {
    this.manager().patchValue({ [name]: value } as TFilterCardValue<Name>);
  }

  /**
   * `appliedValue` can also carry fields the filter card never renders — a page's own state sharing
   * this manager without going through the add/remove chip UI — which must survive an "Update" click
   * untouched. Only the chip-managed (pool) subset gets replaced wholesale by the form's value,
   * including a key dropping out entirely when its chip was removed (a plain spread could only add or
   * overwrite keys, never delete one the form no longer has). Caution for any such non-pool field: it
   * only has a control-less passthrough here, `manager.patchValue({ thatField: ... })` still reaches
   * this method (via `subscribeToValuePatches`), but `formGroup().patchValue(patch)` silently no-ops
   * for a key with no `FormControl`, so the new value never actually lands in `formValue` below — the
   * field's value can only ever change by mutating `manager().initialValue`/`appliedValue` directly
   * from outside `FilterCardComponent`, not via `patchValue`.
   *
   * On the very first apply, `appliedValue()` is still `null` — falling back to `manager().initialValue`
   * is what seeds a non-pool field's starting value into `appliedValue` at all. Without it,
   * `appliedValue` would have no value for that field until something set it directly.
   */
  private mergeIntoAppliedValue(formValue: TFilterCardValue<Name>): TFilterCardValue<Name> {
    const poolFieldNames = this.manager()
      .fields()
      .map((field) => field.name);
    const basePrevious = this.manager().appliedValue() ?? this.manager().initialValue;
    const nonPoolPrevious = omit(basePrevious, poolFieldNames) as TFilterCardValue<Name>;

    return { ...nonPoolPrevious, ...formValue };
  }

  /** True once every *active* field that reports a load status has finished loading its options. */
  private buildFieldReadiness(): Observable<boolean> {
    const activeNames = new Set(this.manager().activeFieldNames());
    const loadStatuses: Observable<ELoadStatus>[] = this.manager()
      .fields()
      .filter((field) => activeNames.has(field.name))
      .map((field: TFilterCardFieldConfig<Name>) =>
        field.type === 'dropdown' && field.loadStatus ? field.loadStatus : of(ELoadStatus.success),
      );

    return loadStatuses.length
      ? combineLatest(loadStatuses).pipe(map((statuses) => statuses.every((status) => status === ELoadStatus.success)))
      : of(true);
  }

  private buildShowApply(): Observable<boolean> {
    const formGroup: FormGroup<Record<string, FormControl>> = this.formGroup();

    return merge(
      this.buildFieldReadiness().pipe(
        filter(Boolean),
        // Options arriving late rewrite placeholder values (see mat-dropdown's hasDelayedDefaultValue),
        // which would otherwise register as a user edit.
        delay(200),
        switchMap(() =>
          this.applied$.pipe(
            map(() => cloneDeep(formGroup.value)),
            startWith(cloneDeep(formGroup.value)),
          ),
        ),
        switchMap((valueSinceLastApply) =>
          formGroup.valueChanges.pipe(map((newValue) => !isEqual(valueSinceLastApply, newValue))),
        ),
      ),
      this.applied$.pipe(map(() => false)),
    ).pipe(startWith(false));
  }

  private subscribeToValuePatches(): void {
    this.manager()
      .valuePatch$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ apply, patch }) => {
        this.formGroup().patchValue(patch);

        if (apply) {
          this.apply();
        }
      });
  }
}
